import { formatMinor, parseQuery, type QueryFilters, type SearchHit } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, cn, Icon, Input, Skeleton, type IconName } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  collectionsQuery,
  createCollection,
  deleteCollection,
  queryEngineKeys,
  runQueryQuery,
} from '../../shared/api/query-queries.js';

/**
 * Everything.
 *
 * One box. Type the way you'd say it, "EMCO receipts over $500 since April"
 *, and the deterministic parser turns it into a structured filter, shows its
 * interpretation back as chips, and runs it over every object in the business.
 * Save the question and it becomes a collection: folders never existed here,
 * and now they never need to.
 */

const HIT_ROUTES: Record<
  SearchHit['entityType'],
  { to: string; param: string; icon: IconName; label: string } | undefined
> = {
  vendor: { to: '/suppliers/$vendorId', param: 'vendorId', icon: 'suppliers', label: 'Supplier' },
  customer: {
    to: '/customers/$customerId',
    param: 'customerId',
    icon: 'customer',
    label: 'Customer',
  },
  job: { to: '/jobs/$jobId', param: 'jobId', icon: 'job', label: 'Job' },
  transaction: {
    to: '/bookkeeping/$recordId',
    param: 'recordId',
    icon: 'bookkeeping',
    label: 'Record',
  },
  document: {
    to: '/documents/$documentId',
    param: 'documentId',
    icon: 'documents',
    label: 'Document',
  },
  equipment: {
    to: '/equipment/$equipmentId',
    param: 'equipmentId',
    icon: 'equipment',
    label: 'Equipment',
  },
  note: undefined,
};

function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delayMs]);
  return debounced;
}

export function EverythingPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/protected/everything' });

  const [text, setText] = useState(search.q ?? '');
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounced = useDebounced(text, 250);
  const { filters, chips } = useMemo(() => parseQuery(debounced), [debounced]);
  const hasQuery = Object.keys(filters).length > 0;

  const results = useQuery({
    ...runQueryQuery(organizationId, filters),
    enabled: hasQuery,
  });
  const collections = useQuery(collectionsQuery(organizationId));

  const save = useMutation({
    mutationFn: () => createCollection(organizationId, saveName.trim(), filters),
    onSuccess: () => {
      setSaving(false);
      setSaveName('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: queryEngineKeys.collections(organizationId) });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That collection could not be saved.'),
  });

  const remove = useMutation({
    mutationFn: (collectionId: string) => deleteCollection(organizationId, collectionId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryEngineKeys.collections(organizationId) }),
  });

  const applyCollection = (name: string, collectionFilters: QueryFilters): void => {
    // Structure re-runs; the box shows an honest reconstruction of it.
    const parts: string[] = [];
    if (collectionFilters.words !== undefined) parts.push(collectionFilters.words.join(' '));
    if (collectionFilters.entityTypes !== undefined)
      parts.push(collectionFilters.entityTypes.join(' '));
    if (collectionFilters.amountMinMinor !== undefined)
      parts.push(`over $${String(Number(collectionFilters.amountMinMinor) / 100)}`);
    if (collectionFilters.amountMaxMinor !== undefined)
      parts.push(`under $${String(Number(collectionFilters.amountMaxMinor) / 100)}`);
    if (collectionFilters.amountAroundMinor !== undefined)
      parts.push(`around $${String(Number(collectionFilters.amountAroundMinor) / 100)}`);
    if (collectionFilters.dateFrom !== undefined) parts.push(`since ${collectionFilters.dateFrom}`);
    setText(parts.join(' '));
    setError(null);
    setSaveName(name);
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      {/* The box is the page. */}
      <div className="pt-8 text-center">
        <h1 className="text-4xl font-bold tracking-tight text-content">Everything</h1>
        <p className="mt-2 text-[15px] text-content-muted">
          Ask the way you'd say it. “EMCO receipts over $500 since April.”
        </p>
      </div>

      <div>
        <Input
          autoFocus
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            void navigate({
              to: '/everything',
              search: event.target.value === '' ? {} : { q: event.target.value },
              replace: true,
            });
          }}
          placeholder="Search every object, amount, and date…"
          className="h-14 text-lg shadow-[var(--shadow-card)]"
        />

        {/* The interpretation: exactly what will run, before it runs. */}
        {chips.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {chips.map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700"
              >
                {chip}
              </span>
            ))}
            {saving ? (
              <span className="flex items-center gap-2">
                <Input
                  value={saveName}
                  onChange={(event) => setSaveName(event.target.value)}
                  placeholder="Name this collection"
                  className="h-8 w-48 text-xs"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => save.mutate()}
                  disabled={saveName.trim() === ''}
                  isLoading={save.isPending}
                >
                  Save
                </Button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setSaving(true)}
                className="text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"
              >
                Save as collection
              </button>
            )}
          </div>
        ) : null}
      </div>

      {error === null ? null : <Alert tone="danger">{error}</Alert>}

      {/* Collections: saved questions, one click from re-asking. */}
      {!hasQuery && (collections.data?.length ?? 0) > 0 ? (
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-content-subtle">
            Collections
          </h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {(collections.data ?? []).map((view) => (
              <span
                key={view.id}
                className="group flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-sm shadow-[var(--shadow-card)]"
              >
                <button
                  type="button"
                  onClick={() => applyCollection(view.name, view.filters)}
                  className="font-medium text-content hover:text-brand-700"
                >
                  {view.name}
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${view.name}`}
                  onClick={() => remove.mutate(view.id)}
                  className="hidden text-content-subtle hover:text-danger group-hover:inline"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {hasQuery ? (
        results.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-3/4" />
          </div>
        ) : results.isError ? (
          <Alert tone="danger" title="The query could not run">
            {results.error.message}
          </Alert>
        ) : results.data.length === 0 ? (
          <p className="py-8 text-center text-sm text-content-muted">
            Nothing matches that. Loosen a chip, the interpretation above is exactly what ran.
          </p>
        ) : (
          <Card>
            <CardBody className="p-0">
              <ul className="divide-y divide-border">
                {results.data.map((hit) => {
                  const route = HIT_ROUTES[hit.entityType];
                  if (route === undefined) return null;
                  return (
                    <li key={`${hit.entityType}:${hit.entityId}`}>
                      <Link
                        to={route.to}
                        params={{ [route.param]: hit.entityId }}
                        className={cn(
                          'flex items-center gap-4 px-5 py-3.5',
                          'transition-colors duration-[var(--duration-quick)] hover:bg-brand-50/60',
                        )}
                      >
                        <Icon name={route.icon} className="size-4 shrink-0 text-content-subtle" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-content">
                            {hit.title ?? 'Untitled'}
                          </span>
                          {hit.snippet === null || hit.snippet === '' ? null : (
                            <span className="block truncate text-xs text-content-subtle">
                              {hit.snippet}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-right">
                          {hit.amountMinor === null ? null : (
                            <span className="tabular block text-sm font-medium text-content">
                              {formatMinor(hit.amountMinor)}
                            </span>
                          )}
                          <span className="block text-xs text-content-subtle">
                            {route.label}
                            {hit.occurredOn === null ? '' : ` · ${hit.occurredOn}`}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </CardBody>
          </Card>
        )
      ) : (collections.data?.length ?? 0) === 0 ? (
        <p className="py-8 text-center text-sm text-content-muted">
          Try “EMCO over $2,000 this year” or “documents since June”, then save the question as a
          collection.
        </p>
      ) : null}
    </div>
  );
}
